// File: src/layouts/CommonHeader.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useLayout } from '../contexts/LayoutContext';
import Button from '../components/common/Button';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import { UserCircleIcon, ArrowLeftOnRectangleIcon, BellIcon, HomeIcon } from '@heroicons/react/24/outline';

// Reusable component to handle adaptive font size
const AdaptiveText: React.FC<{ text: string; baseClassName: string; title: string }> = ({ text, baseClassName, title }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLHeadingElement>(null);
    const [currentFontSize, setCurrentFontSize] = useState<number | null>(null);

    useEffect(() => {
        const adjustFont = () => {
            if (!containerRef.current || !textRef.current) return;
            
            textRef.current.style.fontSize = ''; 
            const initialFontSize = parseFloat(window.getComputedStyle(textRef.current).fontSize);
            let newSize = initialFontSize;

            const isOverflowing = textRef.current.scrollWidth > containerRef.current.offsetWidth;
            
            if (isOverflowing) {
                const MIN_FONT_SIZE = 12;
                while (textRef.current.scrollWidth > containerRef.current.offsetWidth && newSize > MIN_FONT_SIZE) {
                    newSize--;
                    textRef.current.style.fontSize = `${newSize}px`;
                }
            }
            setCurrentFontSize(newSize);
        };

        const timerId = setTimeout(adjustFont, 50);
        window.addEventListener('resize', adjustFont);
        return () => {
            clearTimeout(timerId);
            window.removeEventListener('resize', adjustFont);
        };
    }, [text]);

    return (
      <div ref={containerRef} className="w-full overflow-hidden whitespace-nowrap" title={title}>
        <h1 ref={textRef} className={`${baseClassName} truncate`} style={currentFontSize ? { fontSize: `${currentFontSize}px` } : {}}>
            {text}
        </h1>
      </div>
    );
};


const CommonHeader: React.FC = () => {
  const { t } = useTranslation('common');
  const { user, logout, unreadCount } = useAuth();
  const navigate = useNavigate();
  const { pageTitle, pageIcon, headerDetail } = useLayout();
  
  const [animateBell, setAnimateBell] = useState(false);
  const prevUnreadCountRef = useRef(unreadCount);

  useEffect(() => {
    if (unreadCount > 0 && prevUnreadCountRef.current === 0) {
      setAnimateBell(true);
      const timer = setTimeout(() => setAnimateBell(false), 800);
      return () => clearTimeout(timer);
    }
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentDisplayTitle = pageTitle || "Lifeline Coaching";
  const currentDisplayIcon = pageIcon || <HomeIcon />;

  return (
    <header className="bg-primary shadow-md sticky top-0 z-50">
      <nav className="px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
        
        <div className="flex items-center flex-1 min-w-0">
          <div className="flex-shrink-0 mr-2">
             <Link to="/app">
                {React.cloneElement(currentDisplayIcon, { className: 'h-7 w-7 text-white' })}
             </Link>
          </div>
          <div className="flex flex-col justify-center min-w-0 flex-1">
              <AdaptiveText text={currentDisplayTitle} baseClassName="text-xl sm:text-2xl font-semibold text-white" title={currentDisplayTitle} />
              {headerDetail && (
                <p className="text-xs text-white opacity-80 -mt-1 truncate" title={headerDetail}>
                    {headerDetail}
                </p>
              )}
          </div>
        </div>

        <div className="flex items-center space-x-0 sm:space-x-1 flex-shrink-0">
          {user && (
            <>
              <Link to="/app/notifications" className="relative text-white hover:text-primary-light p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary focus:ring-white" title={t('navigation.notifications')}>
                <BellIcon className={`h-6 w-6 ${animateBell ? 'animate-ring' : ''}`} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
              <LanguageSwitcher 
                variant="minimal" 
                className="text-white hover:text-primary-light mx-2" 
              />
              <Link to="/app/profile" className="text-white hover:text-primary-light p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary focus:ring-white" title={t('navigation.profile')}>
                <UserCircleIcon className="h-7 w-7" />
              </Link>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="text-white border-white hover:bg-white hover:text-primary p-2 flex-shrink-0"
                title={t('navigation.logout')}
              >
                <ArrowLeftOnRectangleIcon className="h-5 w-5" />
                <span className="hidden sm:inline ml-2">{t('navigation.logout')}</span>
              </Button>
            </>
          )}
        </div>
      </nav>
    </header>
  );
};

export default CommonHeader;